import { Schema } from "effect";
import {
  AccountId,
  ConversationId,
  ConversationThreadId,
  ConversationTimelineEventId,
  MessageId,
  ShopperAiTagId,
} from "../../../../ids/definitions";
import {
  MongoCustomerId,
  MongoRuleId,
  MongoUserId,
} from "../../../../ids/mongo-ids";
import { ConversationPriority, ConversationStatus } from "../conversation";
import { MessageChannel } from "../message";

export const TimelineActorType = Schema.Literal(
  "SYSTEM",
  "RULE",
  "USER",
  "CUSTOMER",
  "AI",
  "EXTERNAL_USER",
);
export type TimelineActorType = typeof TimelineActorType.Type;

// Event type literals
export const TIMELINE_EVENT_TYPES = [
  "conversation_created",
  "conversation_status_changed",
  "conversation_priority_changed",
  "conversation_assigned",
  "conversation_read",
  "message_sent",
  "message_received",
  "thread_created",
  "tag_added",
  "tag_removed",
  "rules_triggered",
] as const;

export const TimelineEventType = Schema.Literal(...TIMELINE_EVENT_TYPES);
export type TimelineEventType = typeof TimelineEventType.Type;

// Base fields shared by all timeline events
const BaseTimelineEventFields = {
  id: ConversationTimelineEventId,
  conversationId: ConversationId,
  accountId: AccountId,
  actorType: TimelineActorType,
  actorMongoUserId: Schema.NullOr(MongoUserId),
  actorMongoCustomerId: Schema.NullOr(MongoCustomerId),
  actorMongoRuleId: Schema.NullOr(MongoRuleId),
  parentEventId: Schema.NullOr(ConversationTimelineEventId),
  occurredAt: Schema.Date,
};

// Event data schemas for each event type
export const ConversationCreatedEventData = Schema.Struct({
  status: ConversationStatus,
});
export type ConversationCreatedEventData =
  typeof ConversationCreatedEventData.Type;

export const ConversationStatusChangedEventData = Schema.Struct({
  previousStatus: ConversationStatus,
  newStatus: ConversationStatus,
  reason: Schema.optional(Schema.String),
});
export type ConversationStatusChangedEventData =
  typeof ConversationStatusChangedEventData.Type;

export const ConversationPriorityChangedEventData = Schema.Struct({
  previousPriority: Schema.NullOr(ConversationPriority),
  newPriority: Schema.NullOr(ConversationPriority),
});
export type ConversationPriorityChangedEventData =
  typeof ConversationPriorityChangedEventData.Type;

export const ConversationAssignedEventData = Schema.Struct({
  assignedToMongoUserId: Schema.optional(MongoUserId),
  previousAssigneeMongoUserId: Schema.optional(MongoUserId),
});
export type ConversationAssignedEventData =
  typeof ConversationAssignedEventData.Type;

export const ConversationReadEventData = Schema.Struct({
  readByMongoUserId: MongoUserId,
});
export type ConversationReadEventData = typeof ConversationReadEventData.Type;

export const MessageSentEventData = Schema.Struct({
  messageId: MessageId,
  threadId: ConversationThreadId,
  channel: MessageChannel,
  preview: Schema.optional(Schema.String),
});
export type MessageSentEventData = typeof MessageSentEventData.Type;

export const MessageReceivedEventData = Schema.Struct({
  messageId: MessageId,
  threadId: ConversationThreadId,
  channel: MessageChannel,
  preview: Schema.optional(Schema.String),
});
export type MessageReceivedEventData = typeof MessageReceivedEventData.Type;

export const ThreadCreatedEventData = Schema.Struct({
  threadId: ConversationThreadId,
  channel: MessageChannel,
  externalThreadId: Schema.String,
});
export type ThreadCreatedEventData = typeof ThreadCreatedEventData.Type;

export const TagAddedEventData = Schema.Struct({
  tagId: ShopperAiTagId,
  tagName: Schema.String,
  autoApplied: Schema.optional(Schema.Boolean),
});
export type TagAddedEventData = typeof TagAddedEventData.Type;

export const TagRemovedEventData = Schema.Struct({
  tagId: ShopperAiTagId,
  tagName: Schema.String,
});
export type TagRemovedEventData = typeof TagRemovedEventData.Type;

export const TriggeredRuleInfo = Schema.Struct({
  ruleId: MongoRuleId,
  workflowId: Schema.String,
  ruleName: Schema.String,
});
export type TriggeredRuleInfo = typeof TriggeredRuleInfo.Type;

export const RulesTriggeredEventData = Schema.Struct({
  triggerType: Schema.String, // RuleTriggerType from redo-model, kept as string to avoid circular import
  rules: Schema.Array(TriggeredRuleInfo),
});
export type RulesTriggeredEventData = typeof RulesTriggeredEventData.Type;

// Individual event schemas with discriminator
const ConversationCreatedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("conversation_created"),
  eventData: ConversationCreatedEventData,
});

const ConversationStatusChangedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("conversation_status_changed"),
  eventData: ConversationStatusChangedEventData,
});

const ConversationPriorityChangedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("conversation_priority_changed"),
  eventData: ConversationPriorityChangedEventData,
});

const ConversationAssignedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("conversation_assigned"),
  eventData: ConversationAssignedEventData,
});

const ConversationReadEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("conversation_read"),
  eventData: ConversationReadEventData,
});

const MessageSentEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("message_sent"),
  eventData: MessageSentEventData,
});

const MessageReceivedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("message_received"),
  eventData: MessageReceivedEventData,
});

const ThreadCreatedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("thread_created"),
  eventData: ThreadCreatedEventData,
});

const TagAddedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("tag_added"),
  eventData: TagAddedEventData,
});

const TagRemovedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("tag_removed"),
  eventData: TagRemovedEventData,
});

const RulesTriggeredEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("rules_triggered"),
  eventData: RulesTriggeredEventData,
});

// Discriminated union on eventType
export const ConversationTimelineEvent = Schema.Union(
  ConversationCreatedEvent,
  ConversationStatusChangedEvent,
  ConversationPriorityChangedEvent,
  ConversationAssignedEvent,
  ConversationReadEvent,
  MessageSentEvent,
  MessageReceivedEvent,
  ThreadCreatedEvent,
  TagAddedEvent,
  TagRemovedEvent,
  RulesTriggeredEvent,
);
export type ConversationTimelineEvent = typeof ConversationTimelineEvent.Type;
